import React, { useState, useEffect } from "react";
import { Facebook, Instagram, Globe, Megaphone, Loader2, Users, Eye, MousePointerClick, TrendingUp, BadgeCheck, Heart, Clock, Award, Mail } from "lucide-react";
import { AreaChart, Area, BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Cell } from "recharts";
import { getSocialAnalytics } from "../../api";

const nfmt = (n) => Number(n || 0).toLocaleString();
const gbp = (n) => `\u00A3${Number(n || 0).toLocaleString()}`;

const Stat = ({ icon: Icon, label, value, sub, tint = "#b8935f", testid }) => (
  <div className="bg-white rounded-2xl border border-[#f0ece5] p-5" data-testid={testid}>
    <div className="flex items-center gap-2 mb-2"><Icon size={15} style={{ color: tint }} /><span className="text-[11px] uppercase tracking-wide text-[#a0a0a0]">{label}</span></div>
    <p className="font-display text-[28px] font-500 text-[#2b2b2b] leading-none">{value}</p>
    {sub && <p className="text-[12px] text-[#8a8a8a] mt-1.5">{sub}</p>}
  </div>
);

const ChannelCard = ({ title, icon: Icon, brand, ch, metrics }) => (
  <div className="bg-white rounded-2xl border border-[#f0ece5] p-5">
    <div className="flex items-center justify-between mb-3">
      <div className="flex items-center gap-2">
        <span className="w-8 h-8 rounded-lg flex items-center justify-center text-white" style={{ background: brand }}><Icon size={16} /></span>
        <span className="font-display text-[16px] font-500 text-[#2b2b2b]">{title}</span>
      </div>
      {ch.live
        ? <span className="text-[10px] px-2 py-0.5 rounded-full bg-green-100 text-green-700 flex items-center gap-1" data-testid={`live-${ch.platform}`}><BadgeCheck size={11} /> Live</span>
        : <span className="text-[10px] px-2 py-0.5 rounded-full bg-gray-100 text-gray-500">Sample</span>}
    </div>
    <div className="grid grid-cols-3 gap-2 mb-4">
      {metrics.map((m) => (
        <div key={m.label} className="bg-[#faf7f2] rounded-lg border border-[#f0ece5] p-2.5 text-center">
          <p className="text-[10px] text-[#a0a0a0] uppercase">{m.label}</p>
          <p className="text-[15px] font-500 text-[#2b2b2b]">{m.value}</p>
        </div>
      ))}
    </div>
    <ResponsiveContainer width="100%" height={90}>
      <AreaChart data={ch.series || []} margin={{ top: 4, right: 0, left: 0, bottom: 0 }}>
        <defs>
          <linearGradient id={`g-${ch.platform}`} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={brand} stopOpacity={0.35} />
            <stop offset="100%" stopColor={brand} stopOpacity={0} />
          </linearGradient>
        </defs>
        <Area type="monotone" dataKey="value" stroke={brand} strokeWidth={2} fill={`url(#g-${ch.platform})`} />
        <Tooltip formatter={(v) => [nfmt(v), "Daily"]} labelFormatter={(l) => l} contentStyle={{ borderRadius: 8, border: "1px solid #f0ece5", fontSize: 12 }} />
      </AreaChart>
    </ResponsiveContainer>
    {!ch.live && ch.note && <p className="text-[11px] text-[#a0a0a0] mt-2">{ch.note}</p>}
  </div>
);

const SocialAnalytics = () => {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(true);

  useEffect(() => { getSocialAnalytics().then(setData).catch(() => setData(null)).finally(() => setBusy(false)); }, []);

  if (busy) return <div className="flex items-center justify-center py-24 text-[#8a8a8a]"><Loader2 className="animate-spin mr-2" size={18} /> Loading analytics…</div>;
  if (!data) return <div className="bg-white rounded-2xl border border-[#f0ece5] p-8 text-center text-[#8a8a8a]">Couldn't load analytics.</div>;

  const t = data.totals || {};
  const combined = (data.website?.series || []).map((d) => ({ day: d.day, social: d.value }));
  const fgOk = (data.followerGrowth || []).length >= 2;

  return (
    <div className="space-y-6" data-testid="social-analytics">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Stat icon={MousePointerClick} label="Valuation leads" value={nfmt(t.valuationStarts)} sub="live from your CRM" testid="stat-website-clicks" />
        <Stat icon={Eye} label="Total reach" value={nfmt(t.reach)} sub="Facebook + Instagram (live)" tint="#1877F2" />
        <Stat icon={Users} label="Social followers" value={nfmt(t.followers)} sub="combined audience (live)" tint="#E1306C" />
        <Stat icon={Heart} label="Engagement rate" value={`${data.engagementRate ?? 0}%`} sub={`${nfmt(t.engagements)} interactions`} tint="#b8935f" testid="stat-engagement-rate" />
      </div>

      {combined.length > 0 && (
      <div className="bg-white rounded-2xl border border-[#f0ece5] p-5">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-display text-[17px] font-500 text-[#2b2b2b] flex items-center gap-2"><TrendingUp size={16} className="text-[#b8935f]" /> Website traffic (30 days)</h3>
        </div>
        <ResponsiveContainer width="100%" height={230}>
          <AreaChart data={combined} margin={{ top: 6, right: 8, left: 0, bottom: 0 }}>
            <defs><linearGradient id="gs" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#b8935f" stopOpacity={0.3} /><stop offset="100%" stopColor="#b8935f" stopOpacity={0} /></linearGradient></defs>
            <CartesianGrid strokeDasharray="3 3" stroke="#f0ece5" vertical={false} />
            <XAxis dataKey="day" tick={{ fontSize: 10, fill: "#a0a0a0" }} axisLine={false} tickLine={false} interval={4} />
            <YAxis tick={{ fontSize: 10, fill: "#a0a0a0" }} axisLine={false} tickLine={false} width={40} />
            <Tooltip contentStyle={{ borderRadius: 8, border: "1px solid #f0ece5", fontSize: 12 }} formatter={(v) => [nfmt(v), "Pageviews"]} />
            <Area type="monotone" dataKey="social" stroke="#b8935f" strokeWidth={2} fill="url(#gs)" />
          </AreaChart>
        </ResponsiveContainer>
      </div>
      )}

      {(data.website?.sources || []).length > 0 && (
        <div className="grid lg:grid-cols-2 gap-4" data-testid="traffic-sources">
          <div className="bg-white rounded-2xl border border-[#f0ece5] p-5">
            <h3 className="font-display text-[16px] font-500 text-[#2b2b2b] mb-4 flex items-center gap-2"><TrendingUp size={16} className="text-[#b8935f]" /> Traffic by source</h3>
            <div className="space-y-3">
              {data.website.sources.map((s) => {
                const total = data.website.clicks || 1;
                const pct = Math.round((s.value / total) * 100);
                const colour = { Direct: "#1c1c1e", Google: "#4285F4", Search: "#0F9D58", Social: "#E1306C", Referral: "#b8935f" }[s.name] || "#b8935f";
                return (
                  <div key={s.name} data-testid={`source-${s.name.toLowerCase()}`}>
                    <div className="flex items-center justify-between text-[13px] mb-1">
                      <span className="flex items-center gap-2 text-[#2b2b2b]"><span className="w-2.5 h-2.5 rounded-full" style={{ background: colour }} /> {s.name}</span>
                      <span className="text-[#8a8a8a]">{nfmt(s.value)} · {pct}%</span>
                    </div>
                    <div className="h-2 rounded-full bg-[#f0ece5] overflow-hidden"><div className="h-full rounded-full" style={{ width: `${pct}%`, background: colour }} /></div>
                  </div>
                );
              })}
            </div>
          </div>
          <div className="bg-white rounded-2xl border border-[#f0ece5] p-5">
            <h3 className="font-display text-[16px] font-500 text-[#2b2b2b] mb-4 flex items-center gap-2"><Globe size={16} className="text-[#b8935f]" /> Top pages</h3>
            {(data.website.topPages || []).length === 0 ? <p className="text-[13px] text-[#a0a0a0]">No page data yet.</p> : (
              <div className="space-y-2">
                {data.website.topPages.map((p, i) => (
                  <div key={i} className="flex items-center justify-between p-2.5 rounded-lg bg-[#faf7f2] border border-[#f0ece5]">
                    <span className="text-[13px] text-[#2b2b2b] truncate">{p.path}</span>
                    <span className="text-[12px] text-[#8a8a8a] shrink-0 ml-3">{nfmt(p.value)} views</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      <p className="text-[11px] text-green-600 flex items-center gap-1.5">
        <BadgeCheck size={12} />
        {data.liveSources?.length > 0 ? `Live data: ${data.liveSources.join(", ")}. All figures are real — nothing simulated.` : "Connect Facebook & Instagram to see live insights. All figures are real — nothing simulated."}
      </p>

      <div className="grid md:grid-cols-2 gap-4">
        <ChannelCard title="Facebook" icon={Facebook} brand="#1877F2" ch={data.facebook}
          metrics={[{ label: "Reach", value: nfmt(data.facebook.reach) }, { label: "Impr.", value: nfmt(data.facebook.impressions) }, { label: "Engaged", value: nfmt(data.facebook.engagements) }]} />
        <ChannelCard title="Instagram" icon={Instagram} brand="#E1306C" ch={data.instagram}
          metrics={[{ label: "Reach", value: nfmt(data.instagram.reach) }, { label: "Impr.", value: nfmt(data.instagram.impressions) }, { label: "Followers", value: nfmt(data.instagram.followers) }]} />
        <ChannelCard title="Website" icon={Globe} brand="#b8935f" ch={data.website}
          metrics={[{ label: "Val. leads", value: nfmt(data.website.valuationStarts) }, { label: "Enquiries", value: nfmt(data.website.enquiries) }, { label: "Clicks", value: data.website.clicks == null ? "—" : nfmt(data.website.clicks) }]} />
        <ChannelCard title="Ads" icon={Megaphone} brand="#1c1c1e" ch={data.ads}
          metrics={[{ label: "Clicks", value: nfmt(data.ads.clicks) }, { label: "CPC", value: gbp(data.ads.cpc) }, { label: "CTR", value: `${data.ads.ctr}%` }]} />
      </div>

      {/* ---- Deep dive: follower growth + best time ---- */}
      <div className="grid lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2 bg-white rounded-2xl border border-[#f0ece5] p-5" data-testid="follower-growth">
          <h3 className="font-display text-[16px] font-500 text-[#2b2b2b] mb-3 flex items-center gap-2"><TrendingUp size={16} className="text-[#E1306C]" /> Follower growth</h3>
          {fgOk ? (
          <ResponsiveContainer width="100%" height={200}>
            <AreaChart data={data.followerGrowth || []} margin={{ top: 6, right: 8, left: 0, bottom: 0 }}>
              <defs><linearGradient id="gfg" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#E1306C" stopOpacity={0.3} /><stop offset="100%" stopColor="#E1306C" stopOpacity={0} /></linearGradient></defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0ece5" vertical={false} />
              <XAxis dataKey="label" tick={{ fontSize: 10, fill: "#a0a0a0" }} axisLine={false} tickLine={false} />
              <YAxis tick={{ fontSize: 10, fill: "#a0a0a0" }} axisLine={false} tickLine={false} width={44} domain={["dataMin - 20", "dataMax + 20"]} />
              <Tooltip contentStyle={{ borderRadius: 8, border: "1px solid #f0ece5", fontSize: 12 }} formatter={(v) => [nfmt(v), "Followers"]} />
              <Area type="monotone" dataKey="value" stroke="#E1306C" strokeWidth={2} fill="url(#gfg)" />
            </AreaChart>
          </ResponsiveContainer>
          ) : (
            <div className="h-[200px] flex flex-col items-center justify-center text-center">
              <p className="font-display text-[30px] font-500 text-[#2b2b2b]">{nfmt(t.followers)}</p>
              <p className="text-[12px] text-[#a0a0a0] mt-1">followers today · growth chart builds as we snapshot daily</p>
            </div>
          )}
        </div>
        <div className="bg-white rounded-2xl border border-[#f0ece5] p-5" data-testid="best-time">
          <h3 className="font-display text-[16px] font-500 text-[#2b2b2b] mb-1 flex items-center gap-2"><Clock size={16} className="text-[#b8935f]" /> Best time to post</h3>
          {data.bestTime ? (<>
            <p className="text-[13px] text-[#8a8a8a] mb-3"><span className="text-[#2b2b2b] font-500">Peak</span> around {data.bestTime.window}</p>
            <ResponsiveContainer width="100%" height={150}>
              <BarChart data={data.bestTime.byHour || []} margin={{ top: 4, right: 0, left: -20, bottom: 0 }}>
                <XAxis dataKey="hour" tick={{ fontSize: 9, fill: "#a0a0a0" }} axisLine={false} tickLine={false} />
                <YAxis hide />
                <Tooltip contentStyle={{ borderRadius: 8, border: "1px solid #f0ece5", fontSize: 12 }} cursor={{ fill: "#faf7f2" }} />
                <Bar dataKey="value" radius={[4, 4, 0, 0]}>
                  {(data.bestTime.byHour || []).map((h, i) => {
                    const max = Math.max(...(data.bestTime.byHour || []).map((x) => x.value));
                    return <Cell key={i} fill={h.value === max ? "#b8935f" : "#e2d8c8"} />;
                  })}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </>) : (
            <p className="text-[13px] text-[#a0a0a0] mt-3">Publish a few posts from the app and your best posting times will appear here from real engagement.</p>
          )}
        </div>
      </div>

      {/* ---- Top posts + campaigns ---- */}
      <div className="grid lg:grid-cols-2 gap-4">
        <div className="bg-white rounded-2xl border border-[#f0ece5] p-5" data-testid="top-posts">
          <h3 className="font-display text-[16px] font-500 text-[#2b2b2b] mb-3 flex items-center gap-2"><Award size={16} className="text-[#b8935f]" /> Post results <span className="text-[10px] px-2 py-0.5 rounded-full bg-green-100 text-green-700 font-400 flex items-center gap-1"><BadgeCheck size={11} /> Live</span></h3>
          <div className="space-y-2">
            {(data.topPosts || []).length === 0 ? <p className="text-[13px] text-[#a0a0a0]">No published posts yet — post something from a generator and its real likes, comments & reach show here.</p> : data.topPosts.map((p, i) => (
              <div key={i} className="flex items-center gap-3 p-3 rounded-lg bg-[#faf7f2] border border-[#f0ece5]">
                <span className="w-6 h-6 rounded-full bg-[#b8935f] text-white text-[12px] font-500 flex items-center justify-center shrink-0">{i + 1}</span>
                <div className="min-w-0 flex-1">
                  <p className="text-[13px] text-[#2b2b2b] truncate">{p.caption || "Post"}</p>
                  <p className="text-[11px] text-[#a0a0a0] capitalize flex items-center gap-2">
                    <span>{p.platform}</span>
                    <span className="inline-flex items-center gap-1"><Heart size={10} /> {nfmt(p.likes)}</span>
                    <span>💬 {nfmt(p.comments)}</span>
                  </p>
                </div>
                <div className="text-right shrink-0"><p className="text-[13px] font-500 text-[#2b2b2b]">{nfmt(p.reach)}</p><p className="text-[10px] text-[#a0a0a0]">reach</p></div>
              </div>
            ))}
          </div>
        </div>
        <div className="bg-white rounded-2xl border border-[#f0ece5] p-5" data-testid="campaign-performance">
          <h3 className="font-display text-[16px] font-500 text-[#2b2b2b] mb-3 flex items-center gap-2"><Mail size={16} className="text-[#b8935f]" /> Email campaigns</h3>
          {(data.campaigns || []).length === 0 ? <p className="text-[13px] text-[#a0a0a0]">No campaigns sent yet.</p> : (
            <div className="space-y-2">
              {data.campaigns.map((c, i) => (
                <div key={i} className="p-3 rounded-lg bg-[#faf7f2] border border-[#f0ece5]">
                  <div className="flex items-center justify-between mb-1.5"><p className="text-[13px] font-500 text-[#2b2b2b] truncate">{c.name}</p><span className="text-[11px] text-[#a0a0a0]">{nfmt(c.sent)} sent · {c.status}</span></div>
                  {c.openRate == null ? (
                    <p className="text-[11px] text-[#a0a0a0]">Open & click tracking will populate once recipients engage.</p>
                  ) : (
                    <div className="flex items-center gap-4 text-[12px]"><span className="text-[#8a8a8a]">Open <span className="text-[#2b2b2b] font-500">{c.openRate}%</span></span><span className="text-[#8a8a8a]">Click <span className="text-[#2b2b2b] font-500">{c.clickRate}%</span></span>
                      <div className="flex-1 h-1.5 rounded-full bg-[#eee7db] overflow-hidden"><div className="h-full bg-[#b8935f]" style={{ width: `${Math.min(c.openRate, 100)}%` }} /></div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default SocialAnalytics;
